import { createAction, createReducer, createSelector } from "@reduxjs/toolkit";

import { ReduxRootState } from "types/redux-store";

export interface EntityUser {
  _id: string;
  firstName: string;
  lastName: string;
}

interface OrderedEntityState<T> {
  byId: Record<string, T>;
  allIds: string[];
}

interface ReplaceAllPayload<T> {
  byId: Record<string, T>;
  ids: string[];
}

const USERS_REPLACED = "entityUsers/replaceAll";
const USER_ADDED = "entityUsers/addOne";
const USERS_ADDED = "entityUsers/addMany";
const USER_REPLACED = "entityUsers/replaceOne";
const USER_UPDATED = "entityUsers/updateOne";
const USER_REMOVED = "entityUsers/removeOne";

export const replaceAllUsers = createAction(
  USERS_REPLACED,
  function prepare(usersState: ReplaceAllPayload<EntityUser>) {
    return {
      payload: usersState,
    };
  }
);

export const addUser = createAction<EntityUser>(USER_ADDED);
export const addUsers = createAction<EntityUser[]>(USERS_ADDED);
export const replaceUser = createAction<EntityUser>(USER_REPLACED);
export const updateUser = createAction(
  USER_UPDATED,
  function prepare(user: Partial<EntityUser>) {
    return {
      payload: user,
    };
  }
);
export const removeUser = createAction<string>(USER_REMOVED);

const initialState: OrderedEntityState<EntityUser> = {
  byId: {},
  allIds: [],
};

export const usersEntitiesReducer = createReducer(initialState, (builder) => {
  builder.addCase(replaceAllUsers, (_, action) => ({
    byId: action.payload.byId,
    allIds: action.payload.ids,
  }));
  builder.addCase(addUser, (state, action) => {
    const user = action.payload;

    if (!state.byId[user._id]) {
      state.allIds.push(user._id);
    }

    state.byId[user._id] = user;
  });
  builder.addCase(addUsers, (state, action) => {
    const users = action.payload;

    for (const user of users) {
      if (!state.byId[user._id]) {
        state.allIds.push(user._id);
      }

      state.byId[user._id] = user;
    }
  });
  builder.addCase(replaceUser, (state, action) => {
    const user = action.payload;

    if (!state.byId[user._id]) {
      state.allIds.push(user._id);
    }

    state.byId[user._id] = user;
  });
  builder.addCase(updateUser, (state, action) => {
    const user = action.payload;

    if (!user._id || !state.byId[user._id]) {
      return;
    }

    state.byId[user._id] = {
      ...state.byId[user._id],
      ...user,
    };
  });
  builder.addCase(removeUser, (state, action) => {
    const userId = action.payload;

    if (!state.byId[userId]) {
      return;
    }

    delete state.byId[userId];
    state.allIds = state.allIds.filter((id) => id !== userId);
  });
});

export const selectUsersState = (state: ReduxRootState) => state.entities.users;

export const selectUsersById = createSelector(
  selectUsersState,
  (usersState) => usersState.byId
);

export const selectAllUsers = createSelector(selectUsersState, (usersState) =>
  usersState.allIds
    .map((id) => usersState.byId[id])
    .filter((user): user is EntityUser => Boolean(user))
);
